// Hook para el DashboardKine: lista de pacientes del kine logueado con
// derivaciones (diagnóstico activo, última sesión, adherencia, estado
// operativo) + métricas globales (total, sesiones completadas esta
// semana, adherencia promedio, nombre del kine).
//
// kine_id resuelto via useCurrentKineId() — fallback a TEMP en la
// transición pre-B8 si no hay sesión auth.
//
// ── Adherencia (wireada 23/06/2026, refactorizada Sprint 4 con RPC 0018) ─
// Ventana: '7d' (Q8=A · Dashboard = "esta semana"). Batch inline con
// Promise.all sobre pacienteIds llamando get_adherencia_paciente. Cada
// resultado se interpreta según su `code` + `has_frecuencia`:
//   - ok=true + adherencia_pct numérico → row muestra %, entra al KPI promedio.
//   - has_frecuencia=false / code=VENTANA_MUY_CORTA / NO_PLAN_ACTIVO
//     / NOT_FOUND / error → row muestra —, NO entra al KPI promedio.
//
// Deuda técnica O7 anotada en SPEC A15: N Promise.all satura pool de
// Supabase Free con N>10 pacientes concurrentes. Mitigación futura
// p-limit(5) o RPC batch server-side. No bloqueante para piloto (N<20).
//
// Aggregate del kine = simple average de adherencias no-null (redondeado).
// Sub-decisión aprobada 23/06 (vs weighted) por intuitividad UX.

import { useCallback, useEffect, useState } from "react";
import { supabase } from "../client";
import { useCurrentKineId } from "@/app/components/auth/AuthProvider";
import type { AdherenciaResult } from "./useAdherenciaPaciente";

/** Motivo por el que un paciente NO tiene adherencia numérica en el row.
 *  UI la usa para tooltip específico en lugar del genérico "—". */
export type AdherenciaReason =
  | "sin_frecuencia"
  | "ventana_corta"
  | "sin_plan"
  | "no_encontrado"
  | "error";

export type EstadoPaciente = "Al día" | "En riesgo" | "Atrasado" | "Sin sesiones";

export interface PacienteDashboard {
  id: string;
  nombre: string;
  apellido: string;
  /** diagnostico ?? motivo del tratamiento activo. null si no tiene. */
  diagnostico: string | null;
  /** ISO de la sesión completada más reciente. null si nunca hizo. */
  ultimaSesionAt: string | null;
  /** % de adherencia [0..100] últimos 7 días, o null si el backend no
   *  pudo calcularlo (sin plan / sin frecuencia / ventana corta / error). */
  adherencia: number | null;
  /** Motivo cuando adherencia === null. UI usa para tooltip específico.
   *  null cuando adherencia !== null (no aplica). */
  adherenciaReason: AdherenciaReason | null;
  estado: EstadoPaciente;
  /** "invited" = paciente creado por el kine pero todavía no completó signup
   *  (auth_user_id NULL + signup_token presente). "active" = paciente con
   *  cuenta activa (auth_user_id NOT NULL). El UI lo muestra como badge
   *  para distinguir "invitado pendiente" de "activo sin sesiones recientes". */
  invitationStatus: "invited" | "active";
  /** Solo presente si invitationStatus === "invited". Para "Copiar invitación"
   *  desde quick actions sin requerir un fetch adicional. */
  signupToken: string | null;

  // ── Priority flags (derivados, para filtros en el dashboard) ──────────
  /** Tiene plan activo Y (sin sesiones nunca O última sesión > 5 días).
   *  Más estricto que "Atrasado" (>7d) — captura inactividad temprana. */
  needsReview: boolean;
  /** adherencia !== null && adherencia < 50. Solo aplica a pacientes con
   *  plan activo computable. */
  lowAdherence: boolean;
  /** Última sesión completada con dolor_reportado >= 7. Sin ventana
   *  temporal — captura "alguien con dolor que no debería seguir
   *  entrenando hasta que el kine lo evalúe". */
  hasPain: boolean;
  /** auth_user_id NOT NULL (paciente completó signup) Y 0 sesiones.
   *  "Activó cuenta pero todavía no arrancó". */
  justJoined: boolean;
  /** Mensajes del paciente sin leer por el kine (sender='paciente' AND
   *  read_at IS NULL). Sprint 3 B6: badge per row en DashboardKine. */
  unreadMensajes: number;
}

export interface KineMetricas {
  totalPatients: number;
  /** Promedio simple de adherencias no-null de los pacientes del kine,
   *  redondeado. null si ningún paciente tiene adherencia computable. */
  avgAdherence: number | null;
  /** Sesiones COMPLETADAS del kine en los últimos 7 días. */
  activeSessions: number;
  /** Nombre del kine logueado para el header. */
  kineNombre: string;
}

interface State {
  pacientes: PacienteDashboard[] | null;
  metricas: KineMetricas | null;
  error: string | null;
  loading: boolean;
}

export interface UseKineDashboardReturn extends State {
  /** Re-corre el fetch. Útil después de crear/editar/eliminar paciente
   *  para que la lista refleje el cambio sin recargar página. */
  refetch: () => Promise<void>;
}

interface PacienteRowRaw {
  id: string;
  nombre: string;
  apellido: string;
  auth_user_id: string | null;
  signup_token: string | null;
  tratamientos: Array<{
    diagnostico: string | null;
    motivo: string;
    fecha_inicio: string;
  }>;
  sesiones: Array<{
    ended_at: string | null;
    dolor_reportado: number | null;
  }>;
}

/** Derivación por paciente del retorno de la RPC. null adherencia cuando
 *  el backend no pudo calcular; reason distingue el por qué. */
interface AdherenciaPerPaciente {
  adherencia: number | null;
  reason: AdherenciaReason | null;
}

/** Interpreta el retorno de get_adherencia_paciente. Ver F-01 en SPEC. */
function interpretAdherencia(
  data: AdherenciaResult | null,
  rpcError: string | null,
): AdherenciaPerPaciente {
  if (rpcError) return { adherencia: null, reason: "error" };
  if (!data) return { adherencia: null, reason: "error" };
  // NOT_FOUND / NO_AUTH → mismo mapeo (RLS o auth expiró).
  if (data.code === "NOT_FOUND" || data.code === "NO_AUTH") {
    return { adherencia: null, reason: "no_encontrado" };
  }
  if (data.code === "NO_PLAN_ACTIVO") {
    return { adherencia: null, reason: "sin_plan" };
  }
  if (data.code === "UNKNOWN") {
    return { adherencia: null, reason: "error" };
  }
  if (data.has_frecuencia === false) {
    return { adherencia: null, reason: "sin_frecuencia" };
  }
  if (data.code === "VENTANA_MUY_CORTA") {
    return { adherencia: null, reason: "ventana_corta" };
  }
  if (typeof data.adherencia_pct === "number") {
    return { adherencia: data.adherencia_pct, reason: null };
  }
  return { adherencia: null, reason: "error" };
}

/** Bucketea por adherencia. Apropiado solo cuando expected > 0. */
function deriveEstadoAdherencia(adherencia: number): EstadoPaciente {
  if (adherencia >= 80) return "Al día";
  if (adherencia >= 50) return "En riesgo";
  return "Atrasado";
}

/** Bucketea por frescura de la última sesión completada. Fallback cuando
 *  expected = 0 (paciente sin plan o sin frec medible). */
function deriveEstadoFrescura(ultimaSesionAt: string | null): EstadoPaciente {
  if (!ultimaSesionAt) return "Sin sesiones";
  const days = Math.floor(
    (Date.now() - new Date(ultimaSesionAt).getTime()) / 86_400_000,
  );
  if (days <= 2) return "Al día";
  if (days <= 7) return "En riesgo";
  return "Atrasado";
}

/** Resuelve el estado per paciente. Branch principal: adherencia (si
 *  expected > 0). Branch fallback: frescura (cuando expected = 0). */
function deriveEstado(
  adherencia: number | null,
  ultimaSesionAt: string | null,
): EstadoPaciente {
  if (adherencia !== null) return deriveEstadoAdherencia(adherencia);
  return deriveEstadoFrescura(ultimaSesionAt);
}

export function useKineDashboard(): UseKineDashboardReturn {
  const kineId = useCurrentKineId();
  const [state, setState] = useState<State>({
    pacientes: null,
    metricas: null,
    error: null,
    loading: true,
  });

  const fetchAll = useCallback(
    async (signal?: { cancelled: boolean }) => {
      if (!kineId) return;
      const sieteDiasAtras = new Date(
        Date.now() - 7 * 86_400_000,
      ).toISOString();

      // 4 queries en paralelo (pre-adherencia batch).
      const [
        pacientesResult,
        kineResult,
        sesionesSemanaResult,
        mensajesUnreadResult,
      ] = await Promise.all([
        // 1. Lista pacientes con tratamientos + sesiones completadas embebidas.
        //    auth_user_id + signup_token para invitationStatus + signupToken.
        //    sesiones.dolor_reportado para flag hasPain.
        supabase
          .from("pacientes")
          .select(
            `
            id, nombre, apellido, auth_user_id, signup_token,
            tratamientos(diagnostico, motivo, fecha_inicio, estado),
            sesiones(ended_at, estado, dolor_reportado)
          `,
          )
          .eq("kine_id", kineId)
          .eq("tratamientos.estado", "activo")
          .eq("sesiones.estado", "completada")
          .order("fecha_inicio", {
            foreignTable: "tratamientos",
            ascending: false,
          })
          .order("ended_at", {
            foreignTable: "sesiones",
            ascending: false,
          })
          .returns<PacienteRowRaw[]>(),
        // 2. Nombre del kine para el header.
        supabase
          .from("kines")
          .select("nombre")
          .eq("id", kineId)
          .single(),
        // 3. Count de sesiones COMPLETADAS del kine en últimos 7 días.
        supabase
          .from("sesiones")
          .select("*", { count: "exact", head: true })
          .eq("kine_id", kineId)
          .eq("estado", "completada")
          .gte("started_at", sieteDiasAtras),
        // 4. Mensajes del paciente sin leer (sender='paciente' AND
        //    read_at IS NULL). RLS filtra a los del kine logueado.
        //    Solo trae paciente_id para agrupar count en cliente.
        supabase
          .from("mensajes")
          .select("paciente_id")
          .eq("sender", "paciente")
          .is("read_at", null)
          .returns<{ paciente_id: string }[]>(),
      ]);

      if (signal?.cancelled) return;

      const error =
        pacientesResult.error ||
        kineResult.error ||
        sesionesSemanaResult.error ||
        mensajesUnreadResult.error;
      if (error) {
        console.warn("[useKineDashboard] error", error);
        setState({
          pacientes: null,
          metricas: null,
          error: error.message,
          loading: false,
        });
        return;
      }

      const pacientesRaw = pacientesResult.data ?? [];
      const pacienteIds = pacientesRaw.map((p) => p.id);

      // ── Batch adherencia (RPC 0018, ventana '7d' Q8=A) ─────────────────
      // N calls en Promise.all — deuda O7 anotada. Con N<20 en piloto,
      // dentro del pool de Supabase Free.
      const adherenciaResults = await Promise.all(
        pacienteIds.map((id) =>
          supabase.rpc("get_adherencia_paciente", {
            p_paciente_id: id,
            p_ventana: "7d",
          }),
        ),
      );

      if (signal?.cancelled) return;

      const adherenciaByPaciente = new Map<string, AdherenciaPerPaciente>();
      pacienteIds.forEach((id, idx) => {
        const r = adherenciaResults[idx];
        adherenciaByPaciente.set(
          id,
          interpretAdherencia(
            (r.data as AdherenciaResult | null) ?? null,
            r.error ? r.error.message : null,
          ),
        );
      });

      // Map<paciente_id, unreadMessageCount>.
      const unreadByPaciente = new Map<string, number>();
      for (const row of mensajesUnreadResult.data ?? []) {
        unreadByPaciente.set(
          row.paciente_id,
          (unreadByPaciente.get(row.paciente_id) ?? 0) + 1,
        );
      }

      const pacientes: PacienteDashboard[] = pacientesRaw.map((p) => {
        const tratamiento = p.tratamientos[0];
        const ultimaSesionAt = p.sesiones[0]?.ended_at ?? null;
        const adh = adherenciaByPaciente.get(p.id) ?? {
          adherencia: null,
          reason: "error" as AdherenciaReason,
        };
        const adherencia = adh.adherencia;
        const adherenciaReason = adh.reason;
        const invitationStatus: "invited" | "active" =
          p.auth_user_id === null && p.signup_token !== null
            ? "invited"
            : "active";

        // ── Priority flags ──────────────────────────────────────────────
        const ultimoDolor = p.sesiones[0]?.dolor_reportado ?? null;
        const daysSinceUltimaSesion =
          ultimaSesionAt === null
            ? null
            : Math.floor(
                (Date.now() - new Date(ultimaSesionAt).getTime()) /
                  86_400_000,
              );

        // needsReview: heurística de "kine, revisá esto". Antes usaba
        // expected>0 del cálculo cliente; ahora "tiene adherencia
        // computable" == adherencia !== null (backend confirmó plan+freq).
        const needsReview =
          adherencia !== null &&
          (daysSinceUltimaSesion === null || daysSinceUltimaSesion > 5);

        const lowAdherence = adherencia !== null && adherencia < 50;

        const hasPain = ultimoDolor !== null && ultimoDolor >= 7;

        const justJoined =
          p.auth_user_id !== null && p.sesiones.length === 0;

        const unreadMensajes = unreadByPaciente.get(p.id) ?? 0;

        return {
          id: p.id,
          nombre: p.nombre,
          apellido: p.apellido,
          // "Contenedor automático" es el motivo técnico que pone la RPC
          // 0017 al autocrear el tratamiento: no es un diagnóstico.
          diagnostico:
            tratamiento?.diagnostico ??
            (tratamiento?.motivo === "Contenedor automático" ? null : tratamiento?.motivo) ??
            null,
          ultimaSesionAt,
          adherencia,
          adherenciaReason,
          estado: deriveEstado(adherencia, ultimaSesionAt),
          invitationStatus,
          signupToken:
            invitationStatus === "invited" ? p.signup_token : null,
          needsReview,
          lowAdherence,
          hasPain,
          justJoined,
          unreadMensajes,
        };
      });

      // Avg simple de adherencias no-null. null si todos los pacientes
      // tienen adherencia null.
      const conAdherencia = pacientes
        .map((p) => p.adherencia)
        .filter((a): a is number => a !== null);
      const avgAdherence =
        conAdherencia.length > 0
          ? Math.round(
              conAdherencia.reduce((a, b) => a + b, 0) / conAdherencia.length,
            )
          : null;

      const metricas: KineMetricas = {
        totalPatients: pacientes.length,
        avgAdherence,
        activeSessions: sesionesSemanaResult.count ?? 0,
        kineNombre: kineResult.data?.nombre ?? "—",
      };

      setState({ pacientes, metricas, error: null, loading: false });
    },
    [kineId],
  );

  useEffect(() => {
    if (!kineId) return;
    const signal = { cancelled: false };
    setState((s) => ({ ...s, loading: true }));
    void fetchAll(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [kineId, fetchAll]);

  const refetch = useCallback(async () => {
    if (!kineId) return;
    setState((s) => ({ ...s, loading: true }));
    await fetchAll();
  }, [kineId, fetchAll]);

  return { ...state, refetch };
}
